import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { TranslateService } from '@ngx-translate/core';
import { FilterRequestForCombo, RolesService, UsersService } from '@src/app/shared/backend/clients';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { ExportService } from '@src/app/shared/services/export.service';
import { FormGroup } from '@angular/forms';
import { FiltersPanelChangeEvent, FiltersPanelDropdownLazyLoadEvent, FiltersPanelFieldOverrides } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { buildRemoteDropdownRequest, normalizeDropdownState } from '@src/app/shared/components/filters-panel/filters-panel-dropdown.helpers';
import { ActiveInactivePipe } from '@app/shared/pipes/activeInactive.pipe';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrl: './users.component.scss',
    host: {
        class: 'app-users'
    },
    standalone: false
})
export class UsersComponent extends BaseListComponent implements OnInit, OnDestroy {

  private initialLoadDone = false;
  private readonly componentDestroyed$ = new Subject<void>();
  public readonly usersTableName = TableSettingService.UsersTableName;

  public filtersPanelFieldOverrides: FiltersPanelFieldOverrides = {};

  // Dropdown: roles
  roles = {
    results: [],
    pageIndex: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false
  };

  // Dropdown: isActive
  statusOptions: Array<{ id: string | number; text: string }> = [];

  exports: boolean = false;
  currentDate: Date = new Date();
  displayExportModalbox: boolean = false;
  allowNewUser: boolean = false;

  constructor(
    private router: Router,
    public permissionService: PermissionService,
    public override tableSettingService: TableSettingService,
    private translateService: TranslateService,
    private toasterService: ToasterService,
    private exportService: ExportService,
    private usersService: UsersService,
    private rolesService: RolesService) {
    super()
  }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.restorePaginatorStateFromStorage();

    this.exports = await this.permissionService.hasActionPermissions(Actions.export_users);
    this.allowNewUser = await this.permissionService.hasActionPermissions(Actions.create_users);

    // i18n propagation on lang change
    this.rebuildStatusOptions();

    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.rebuildStatusOptions();
        this.rebuildFiltersPanelOverrides();

        // Update sort labels without resetting the current sort selection
        this.rebuildSortPanelColumnLabels();
      });

    await this.tableSettingService.initUsers();
    await super.setCache(this.usersTableName);

    // default sort for stable pagination + 18n fn labelResolver (sort dropdown options)
    this.initSortPanel({
      defaultSort: {
        key: 'username',
        direction: 'ASC'
      },
      labelResolver: (col) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        if (translated && translated !== translationKey) {
          return translated;
        }

        return col.header;
      }
    });

    this.rebuildSortPanelColumnLabels();
    this.rebuildFiltersPanelOverrides();

    // Fallback: if the panel doesn't restore anything, load unfiltered!!
    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.get();
      }
    });
  }

  override async get() {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.page = await firstValueFrom(this.usersService.apiUsersFilterTablePost(this.filter));
    this.data = this.page.results;
  }

  override onPageChange(event: any): void {
    this.initialLoadDone = true;
    super.onPageChange(event);
  }

  protected override onCriteriaResetRequested(): void {
    this.resetPaginatorStorage();
  }

  private resetPaginatorStorage(): void {
    resetPaginatorStateToFirstPage({
      tableName: this.usersTableName,
      pageSize: this.pageSize,
    });
  }

  private restorePaginatorStateFromStorage(): void {
    const stored = loadStoredPaginatorState(this.usersTableName);

    if (!stored) {
      return;
    }

    const storedPageNumber = stored.valuesByFieldKey.pageNumber;
    const storedPageSize = stored.valuesByFieldKey.pageSize;

    this.pageNumber = storedPageNumber;
    this.pageSize = storedPageSize;

    this.filter.pageNumber = storedPageNumber;
    this.filter.pageSize = storedPageSize;
  }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent): Promise<void> {
    // Important: pass all table cols (not only visible ones) to prevent hidden col filters from affecting results
    const currentFilters = this.filter.filters as any;

    const nextFilters = applyFiltersPanelChange(
      evt,
      currentFilters,
      this.allTableColumns
    );

    if (!nextFilters) {
      return;
    }

    this.filter.filters = nextFilters as any;

    const source = evt.source;
    const isApplyAction = source === 'apply';
    const isClearAction = source === 'clear';
    const shouldResetPaging = isApplyAction || isClearAction;

    if (shouldResetPaging) {
      const firstPageNumber = 1;

      this.pageNumber = firstPageNumber;
      this.filter.pageNumber = firstPageNumber;

      this.onCriteriaResetRequested();
    }

    this.initialLoadDone = true;
    await this.get();
  }

  async onFiltersPanelDropdownLazyLoad(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
    if (!evt) return;

    const handlers: Record<string, (e: FiltersPanelDropdownLazyLoadEvent) => Promise<void>> = {
      roleId: (e) => this.loadRolesCombo(e),
    };

    const handler = handlers[evt.fieldKey];
    if (!handler) return;

    await handler(evt);
  }

  private async loadRolesCombo(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
    const requestPayload: FilterRequestForCombo = buildRemoteDropdownRequest({
      pageIndex: evt.pageIndex,
      pageSize: evt.pageSize,
      search: evt.search
    });

    const response = await firstValueFrom(
      this.rolesService.apiRolesFilterComboPost(requestPayload)
    );

    const normalized = normalizeDropdownState(response as any, evt.pageIndex) as any;

    // Map to dropdown format
    const results = Array.isArray(normalized?.results) ? normalized.results : [];
    normalized.results = results.map((role: any) => ({
      id: role?.id,
      text: role?.text ?? role?.name ?? role?.label ?? `#${role?.id}`
    }));

    this.roles = normalized;
    this.rebuildFiltersPanelOverrides();
  }

  private rebuildStatusOptions() {
    const pipe = new ActiveInactivePipe(this.translateService);
    this.statusOptions = pipe.values;
  }

  private rebuildFiltersPanelOverrides(): void {
    this.filtersPanelFieldOverrides = {
      roleId: {
        fieldLabel: 'TABLE.HEADER.UserRoles',
        isMultiselect: true,

        options: this.roles.results ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchPlaceholder: 'Search...',
        searchMode: 'remote',

        showSearchbox: true,
        showClear: true,
        compactUi: true,

        pageSize: 10,
        totalCount: this.roles.totalCount ?? 0,
        totalPages: this.roles.totalPages ?? 1,
        pageIndex: this.roles.pageIndex ?? 1,
        hasPreviousPage: this.roles.hasPreviousPage ?? false,
        hasNextPage: this.roles.hasNextPage ?? false
      },

      isActive: {
        fieldLabel: 'TABLE.HEADER.Status',
        isMultiselect: true,

        options: this.statusOptions,
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      }
    };
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    // Update sort labels without resetting the current sort selection
    const nextSortableColumns = visibleColumns
      .filter((col: any) => {
        const field = String(col?.field ?? '').trim();
        return (
          field.length > 0 &&
          field.toLowerCase() !== 'actions' &&
          col?.sortable !== false
        );
      })
      .map((col: any) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        let label;
        if (translated && translated !== translationKey) {
          label = translated;
        } else {
          label = col.header;
        }

        return {
          key: col.field,
          label
        };
      });

    this.sortableColumns = nextSortableColumns;

    // clear active sort if points to col that is not available due to system settings update
    const allowedKeys = new Set(nextSortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    }
  }

  goToCreateUserForm() {
    this.router.navigate(['/users/add']);
  }

  goToUserDetails(event: any, userId: string) {
    this.router.navigate([`/users/${userId}`]);
  }

  onOpenExportModalbox() {
    this.displayExportModalbox = true;
  }

  handleExportSubmit(form: FormGroup) {
    const range = form.value.dateRange;
    const fileName = form.value.exportName;
    const fileType = form.value.format;

    this.exportService.exportData(
      this.filter,
      [range?.[0], range?.[1] ?? new Date()],
      this.usersService.apiUsersExportPost.bind(this.usersService),
      {
        fileName,
        fileType,
        onSuccess: () => {
          const title = this.translateService.instant('EXPORT_SETTINGS.TOAST_SUCCESS_TITLE');
          const message = this.translateService.instant('EXPORT_SETTINGS.TOAST_SUCCESS_MESSAGE');
          this.toasterService.show('success', title, message);
        },
        onError: (error) => {
          const title = this.translateService.instant('EXPORT_SETTINGS.TOAST_ERROR_TITLE');
          const message = this.translateService.instant('EXPORT_SETTINGS.TOAST_ERROR_MESSAGE');
          this.toasterService.show('error', title, message);
          console.error('Export error:', error);
        }
      }
    );
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
